import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { AlertTriangle, CircleHelp, FileText } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api, background, changedElsewhere } from "../../api/client";
import type { DtoBand, Phase, SituationBoard, SituationCell } from "../../api/types";
import { Callout } from "../../components/Callout";
import { Switch } from "../../components/Checkbox";
import { EmptyState } from "../../components/EmptyState";
import { IncidentCode } from "../../components/IncidentCode";
import { motion, Presence } from "../../components/presence";
import { Badge, Button, IconButton, SectionLabel, textareaClass } from "../../components/ui";
import { unitDesignation } from "../../i18n/designation";
import { k, locale, t } from "../../i18n/i18n";
import { term, unitMissionName } from "../../i18n/vocabulary";
import { bandText, CAPOPS_FULL, colorTone, dtoColor, DTO_FULL, familyLabel, maintenanceColor } from "../../maintenance";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { StatusIcon } from "./IncidentPanel";

/** Point de situation (tableau de bord « Point / Appréciation de situation » de l'iPad) : mission et idée de
 *  manœuvre de l'OPO, unités × temps (mission du temps, effort, incidents), point par cellule du centre
 *  opérations — état, note, alerte, question, activation ; DTO et CAPOPS pour la cellule logistique, pré-remplies par
 *  le calcul pour la direction de l'exercice et corrigibles — relié aux notes 3A des incidents de chaque cellule.
 *  Partagé par tous les postes (actualisation toutes les 5 s). */

const TEMPOS: Phase[] = ["T1", "T2", "T3"];
const STATES = [
  { value: -1, label: k("Non évaluée"), dot: "bg-line-strong" },
  { value: 0, label: k("Verte"), dot: "bg-ok" },
  { value: 1, label: k("Orange"), dot: "bg-warn" },
  { value: 2, label: k("Rouge"), dot: "bg-danger" },
];
// Couleurs partagées avec le serveur, propres à chaque indicateur : la CAPOPS aux seuils de l'iPad (vert au-dessus de
// 80 %, rouge à 60 % et en dessous), la DTO face à la DTO attendue du temps (orange sous sa borne basse de moins de
// 5 points, rouge au-delà ; au-dessus, jamais une alerte).
const TONE_TEXT = { ok: "text-ok", warn: "text-warn", danger: "text-danger", neutral: "text-ink-muted" } as const;
const capopsTone = (v: number | null) => TONE_TEXT[colorTone(maintenanceColor(v))];

/** DTO ou CAPOPS de la cellule logistique : la saisie (animateur ou cellule) ou, sans saisie, la valeur calculée.
 *  Saisie et calcul côte à côte (« saisi 70 % · calculé 78 % ») avec « Revenir au calcul » quand ils coexistent.
 *  DTO : `band`, la DTO attendue du temps, marquée sur le curseur et écrite dessous. */
function Percent({
  label,
  full,
  value,
  computed,
  computedHint,
  disabled,
  editable,
  onCommit,
  onRevert,
  band,
  reading,
}: {
  label: string;
  full: string;
  value: number | null;
  computed: number | null;
  computedHint: string;
  disabled: boolean;
  editable: boolean;
  onCommit: (v: number) => void;
  onRevert: () => void;
  band?: DtoBand | null;
  /** Lecture propre à l'indicateur, sous le curseur (« attendue 60 à 75 % », « vert au-dessus de 80 % »). */
  reading: string;
}) {
  const shown = value ?? computed;
  const [local, setLocal] = useState(shown ?? 100);
  useEffect(() => setLocal(shown ?? 100), [shown]);
  const overridden = value !== null && computed !== null;
  // Un appui sans déplacement n'enregistre rien : la valeur calculée n'est pas figée en saisie par mégarde.
  const commit = () => {
    if (shown === null || local !== shown) onCommit(local);
  };
  return (
    <div className="min-w-0">
      <label className="block">
        <span className="flex items-baseline justify-between gap-2 text-meta">
          <span className="min-w-0 truncate font-semibold text-ink-soft" title={full}>{label}</span>
          <span
            className={clsx(
              "shrink-0 font-semibold tabular-nums transition-colors",
              shown === null ? TONE_TEXT.neutral : band ? TONE_TEXT[colorTone(dtoColor(local, band.low))] : capopsTone(local),
            )}
          >
            {shown === null ? "—" : t("{n} %", { n: local })}
          </span>
        </span>
        <span className="relative mt-1 block">
          {band && (
            // Fourchette de la DTO attendue, sous le curseur.
            <span aria-hidden className="pointer-events-none absolute inset-y-1 rounded-sm bg-ok/15"
              style={{ left: `${band.low}%`, width: `${Math.max(0, band.high - band.low)}%` }} />
          )}
          <input type="range" min={0} max={100} step={1} value={local} disabled={disabled} aria-label={full}
            className="relative block w-full accent-accent"
            onChange={(e) => setLocal(Number(e.target.value))} onPointerUp={commit} onKeyUp={commit} />
        </span>
      </label>
      <p className="mt-0.5 text-meta leading-snug text-ink-muted tabular-nums">{reading}</p>
      {computed !== null && (
        <div className="mt-0.5 min-h-10 text-meta leading-snug text-ink-muted">
          {overridden ? (
            <div key="overridden" className={motion("fade")}>
              <span className="block tabular-nums" title={computedHint}>{t("saisi {entered} % · calculé {computed} %", { entered: value, computed })}</span>
              {editable && (
                <Button variant="link" size="sm" onClick={onRevert} disabled={disabled}>
                  {t("Revenir au calcul")}
                </Button>
              )}
            </div>
          ) : (
            <span key="computed" className={clsx("block", motion("fade"))} title={computedHint}>{t("Calculée")}</span>
          )}
        </div>
      )}
    </div>
  );
}

type CellChanges = Partial<Pick<SituationCell, "color" | "note" | "alert" | "question" | "enabled" | "dto" | "capops">> & {
  base?: string | null;
};
type CellHandlers = { onSaved?: () => void; onConflict?: () => void; onFailed?: () => void };

export function CellCard({ cell, busy, onChange }: { cell: SituationCell; busy: boolean; onChange: (changes: CellChanges, handlers?: CellHandlers) => void }) {
  const locked = busy || !cell.editable;
  const computed = cell.computed ?? null;
  const dtoHint = computed?.family
    ? t("Calculée : famille de matériel la plus en retard sur la DTO attendue à {phase} ({family})", {
        phase: computed.phase,
        family: familyLabel(computed.family),
      })
    : t("Calculée");
  const capopsHint = computed ? t("Calculée : moyenne des unités à {phase}, pondérée par leur parc", { phase: computed.phase }) : t("Calculée");
  const state = STATES.find((s) => s.value === cell.color) ?? STATES[0]!;
  const next = STATES[(STATES.indexOf(state) + 1) % STATES.length]!;
  const [note, setNote] = useState(cell.note);
  const editing = useRef(false);
  // Point lu quand la saisie de la note a commencé : envoyé avec elle, le serveur refuse si un autre poste a modifié
  // le point entre-temps. La note refusée reste dans le champ (`conflict`) jusqu'au choix du poste.
  const base = useRef<string | null>(cell.updated_at);
  const [conflict, setConflict] = useState(false);
  // Note refusée (réseau, licence, stockage) : jamais affichée comme enregistrée ; elle reste dans le champ, marquée
  // « non enregistrée », avec « Réessayer » (audit de persistance m11).
  const [failed, setFailed] = useState(false);
  // Curseur refusé, pour toute raison (réseau, licence, point modifié par un autre poste) : il revient à la valeur
  // du serveur (remonté avec elle), même quand l'autre poste n'a pas changé ce réglage.
  const [reverts, setReverts] = useState(0);
  useEffect(() => {
    if (!editing.current && !conflict && !failed) setNote(cell.note);
  }, [cell.note, conflict, failed]);
  // Réglages ponctuels (état, alerte, question, activation, DTO, CAPOPS) : partis du point affiché.
  const revert = () => setReverts((n) => n + 1);
  const set = (changes: CellChanges) => onChange({ ...changes, base: cell.updated_at }, { onFailed: revert, onConflict: revert });
  const saveNote = (from: string | null) =>
    onChange(
      { note, base: from },
      {
        onSaved: () => {
          setConflict(false);
          setFailed(false);
        },
        onConflict: () => setConflict(true),
        onFailed: () => setFailed(true),
      },
    );
  return (
    <div className={clsx("flex flex-col gap-2 rounded-xl border bg-surface px-3.5 py-3 shadow-card transition-opacity", cell.enabled ? "border-line" : "border-dashed border-line opacity-60")}>
      <div className="flex items-center gap-2">
        {/* État de la cellule : bouton qui fait tourner les couleurs ; en lecture seule, la pastille seule, pleinement
            lisible (pas d'atténuation de bouton désactivé). */}
        {cell.editable ? (
          <IconButton size="sm" onClick={() => set({ color: next.value })} disabled={locked || !cell.enabled}
            title={t("État : {state} — cliquer pour « {next} »", { state: t(state.label).toLowerCase(), next: t(next.label).toLowerCase() })}
            aria-label={t("État : {state}", { state: t(state.label).toLowerCase() })}>
            <span className={clsx("size-3.5 rounded-full ring-1 ring-line transition-colors", state.dot)} />
          </IconButton>
        ) : (
          <span role="img" className="flex size-7 shrink-0 items-center justify-center" title={t("État : {state}", { state: t(state.label).toLowerCase() })}
            aria-label={t("État : {state}", { state: t(state.label).toLowerCase() })}>
            <span className={clsx("size-3.5 rounded-full ring-1 ring-line transition-colors", state.dot)} />
          </span>
        )}
        <span className="flex-1 truncate text-title font-semibold text-ink">{term(cell.cellule)}</span>
        {/* Alerte et question levées : bouton actif, le ton porté par l'icône. */}
        <IconButton size="sm" active={cell.alert} onClick={() => set({ alert: !cell.alert })} disabled={locked || !cell.enabled}
          title={cell.alert ? t("Retirer l'alerte") : t("Signaler une alerte")}
          aria-label={cell.alert ? t("Retirer l'alerte") : t("Signaler une alerte")}
          aria-pressed={cell.alert}>
          <AlertTriangle size={14} className={cell.alert ? "text-warn" : undefined} />
        </IconButton>
        <IconButton size="sm" active={cell.question} onClick={() => set({ question: !cell.question })} disabled={locked || !cell.enabled}
          title={cell.question ? t("Retirer la question") : t("Signaler une question")}
          aria-label={cell.question ? t("Retirer la question") : t("Signaler une question")}
          aria-pressed={cell.question}>
          <CircleHelp size={14} className={cell.question ? "text-accent" : undefined} />
        </IconButton>
        <Switch
          className="ml-1"
          checked={cell.enabled}
          disabled={locked}
          onChange={(enabled) => set({ enabled })}
          aria-label={cell.enabled ? t("Désactiver la cellule") : t("Activer la cellule")}
          title={
            !cell.editable
              ? cell.enabled
                ? t("Cellule active")
                : t("Cellule désactivée")
              : cell.enabled
                ? t("Désactiver la cellule")
                : t("Activer la cellule")
          }
        />
      </div>
      <div className="flex items-center gap-2 text-meta text-ink-muted">
        <span>{cell.incidents === 0 ? t("Aucun incident") : plural(cell.incidents, t("incident"), t("incidents"))}</span>
        {cell.aar !== null && (
          <span className="ml-auto font-medium text-ink-soft" title={t("Moyenne 3A des incidents de la cellule")}>
            {t("3A")} {cell.aar.toLocaleString(locale(), { maximumFractionDigits: 1 })}/5
          </span>
        )}
      </div>
      {cell.logistics && (
        <div className="grid grid-cols-2 gap-3">
          <Percent key={`dto-${reverts}`} label={t("DTO")} full={t(DTO_FULL)} value={cell.dto} computed={computed?.dto ?? null}
            computedHint={dtoHint} disabled={locked || !cell.enabled} editable={cell.editable} band={computed?.expected}
            reading={computed?.expected ? t("attendue {band}", { band: bandText(computed.expected) }) : t("jugée contre la DTO attendue")}
            onCommit={(v) => set({ dto: v })} onRevert={() => set({ dto: null })} />
          <Percent key={`capops-${reverts}`} label={t("CAPOPS")} full={t(CAPOPS_FULL)} value={cell.capops}
            computed={computed?.capops ?? null} computedHint={capopsHint} disabled={locked || !cell.enabled} editable={cell.editable}
            reading={t("vert au-dessus de 80 %")}
            onCommit={(v) => set({ capops: v })} onRevert={() => set({ capops: null })} />
        </div>
      )}
      <textarea rows={2} value={note} placeholder={cell.editable ? t("Note…") : ""} disabled={!cell.enabled} readOnly={!cell.editable}
        onFocus={() => {
          editing.current = true;
          if (!conflict) base.current = cell.updated_at;
        }}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => {
          editing.current = false;
          if (note !== cell.note && !conflict) saveNote(base.current);
        }}
        className={clsx(textareaClass, "min-h-11 placeholder:text-ink-muted")} />
      <Presence show={conflict}>
        {conflict && (
          <Callout
            tone="warn"
            title={t("Note modifiée par un autre poste")}
            action={
              <span className="flex items-center gap-2">
                <Button variant="link" onClick={() => { setNote(cell.note); setConflict(false); }}>{t("Voir la sienne")}</Button>
                <Button variant="link" onClick={() => saveNote(cell.updated_at)} disabled={busy}>{t("Garder la mienne")}</Button>
              </span>
            }
          >
            {t("La vôtre est gardée dans le champ.")}
          </Callout>
        )}
      </Presence>
      <Presence show={failed && !conflict}>
        {failed && !conflict && (
          <Callout
            tone="warn"
            title={t("Note non enregistrée")}
            action={
              <span className="flex items-center gap-2">
                <Button variant="link" onClick={() => saveNote(base.current)} disabled={busy}>{t("Réessayer")}</Button>
                <Button variant="link" onClick={() => { setNote(cell.note); setFailed(false); }}>{t("Revenir à la note enregistrée")}</Button>
              </span>
            }
          >
            {t("Elle reste dans le champ.")}
          </Callout>
        )}
      </Presence>
    </div>
  );
}

export function SituationPanel({ exerciseId, opo }: { exerciseId: string; opo: number | undefined }) {
  const setView = useUi((s) => s.setView);
  const notify = useUi((s) => s.notify);
  const queryClient = useQueryClient();
  const key = ["situation", exerciseId, opo ?? 1] as const;
  const query = useQuery({
    queryKey: key,
    queryFn: () => background(() => api.situationBoard(exerciseId, opo)),
    refetchInterval: 5_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const mutation = useMutation({
    mutationFn: ({ cellule, changes }: { cellule: string; changes: CellChanges }) => api.updateCell(exerciseId, cellule, changes, opo),
    onSuccess: (next: SituationBoard) => queryClient.setQueryData(key, next),
    onError: (e: Error) => {
      // Point modifié entre-temps par un autre poste : rien n'est écrasé, le point est relu.
      if (changedElsewhere(e)) {
        notify({ text: t("Modifié par un autre poste"), detail: e.message, tone: "warn" });
        void queryClient.invalidateQueries({ queryKey: key });
        return;
      }
      notify({ text: t("Point de situation"), detail: e.message, tone: "error" });
    },
  });
  const data = query.data;
  if (!data) {
    return <EmptyState variant="loading" title={t("Chargement du point de situation…")} />;
  }
  return (
    <div className={clsx("space-y-5 px-5 py-4 max-sm:px-4", motion("fade"))}>
      <div className="grid gap-4 md:grid-cols-2">
        {(
          [
            [t("Rappel de la mission"), data.mission],
            [t("Idée de manœuvre"), data.maneuver],
          ] as const
        ).map(([title, text]) => (
          <section key={title} className="rounded-xl border border-line bg-surface px-4 py-3 shadow-card">
            <SectionLabel as="h3" inset className="mb-1.5">{title}</SectionLabel>
            {text ? (
              <p className="text-justify text-title leading-relaxed text-ink hyphens-auto">{text}</p>
            ) : (
              <p className="text-ui text-ink-muted">
                {t("Rédigez l'OPO pour l'afficher ici.")}{" "}
                <Button variant="link" onClick={() => setView("order")}>
                  <FileText size={14} /> {t("Ordre d'opérations")}
                </Button>
              </p>
            )}
          </section>
        ))}
      </div>

      <section className="overflow-x-auto rounded-xl border border-line bg-surface shadow-card">
        <table className="w-full min-w-[640px] table-fixed">
          <thead>
            <tr className="border-b border-line bg-canvas/60 text-left">
              <SectionLabel as="th" inset className="w-[200px] px-3 py-2">{t("Unité")}</SectionLabel>
              {TEMPOS.map((t) => {
                const phase = data.phases.find((p) => p.phase === t);
                return (
                  <th key={t} className="px-3 py-2">
                    <span className="font-mono text-ui font-semibold text-ink">{t}</span>
                    {phase?.start_dtg && <span className="ml-2 font-mono text-meta font-normal text-ink-muted tabular-nums">{phase.start_dtg}</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {data.rows.map((row) => (
              <tr key={row.key} className="align-top">
                <td className="px-3 py-2 text-ui font-semibold text-friend">
                  {row.commander ? t("{label} (CO)", { label: unitDesignation(row.label) }) : unitDesignation(row.label)}
                </td>
                {TEMPOS.map((tempo) => {
                  const phase = row.phases[tempo];
                  const incidents = row.incidents[tempo] ?? [];
                  return (
                    <td key={tempo} className="px-3 py-2">
                      <div className={clsx("text-ui leading-snug", phase.effort ? "font-semibold text-ink" : "text-ink-soft")}>
                        {phase.mission == null ? "—" : unitMissionName(phase.mission)}
                        {phase.effort && (
                          <Badge size="sm" tone="accent" className="ml-1.5">
                            {t("Effort")}
                          </Badge>
                        )}
                      </div>
                      {incidents.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {incidents.map((i) => (
                            <span key={i.id} className="inline-flex items-center gap-1">
                              <IncidentCode kind={i.type} code={i.code} />
                              <StatusIcon status={i.status} size={12} />
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <div className="mb-2 flex items-baseline justify-between">
          <SectionLabel as="h3" inset>{t("Point par cellules")}</SectionLabel>
          <span className="text-meta text-ink-muted">
            {t("Pastille : non évaluée · verte · orange · rouge")}
            {data.role === "animateur"
              ? ` ${t("(clic pour changer)")}`
              : data.role === "cellule"
                ? ` — ${t("vous renseignez votre cellule")}`
                : ` — ${t("consultation seule")}`}
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {data.cells.map((cell) => (
            <CellCard key={cell.cellule} cell={cell} busy={mutation.isPending}
              onChange={(changes, handlers) =>
                mutation.mutate(
                  { cellule: cell.cellule, changes },
                  {
                    onSuccess: () => handlers?.onSaved?.(),
                    onError: (e) => (changedElsewhere(e) ? handlers?.onConflict?.() : handlers?.onFailed?.()),
                  },
                )
              } />
          ))}
        </div>
      </section>
    </div>
  );
}
